/**
 * The loader's two mount points, `#root` and `#zion-maintenance` (layered shell surfaces, D18,
 * R-8, R-9, R-50, WS-6 §3.8).
 *
 * Every mount goes through the runtime's `mountSurface`: system pages, the root shell and the
 * interim root alike, so each has one containment, one CSS scoper and one services path.
 *
 * Each mount gets its own wrapper `div[data-zion-mount]` inside the mount point. R-50 lets a
 * surface's cleanup hold its element for up to one second after `unmount`, and the runtime refuses
 * a second mount into a busy element. So a swap hides the outgoing wrapper (still attached, so the
 * surface's cleanup sees its nodes) and removes it once the runtime has emptied it, or after
 * {@link WRAPPER_REMOVAL_MS}, whichever comes first, while the incoming surface mounts into a fresh
 * wrapper at once.
 *
 * The incoming wrapper stays hidden, and the boot indicator shows with `aria-busy="true"` on
 * `#root`, until the incoming mount's `ready` settles, so the indicator covers the bundle download
 * (design-1). This holds on every swap of `#root`, not on the first one only (R-62): `reveal` hides
 * the indicator and sets `aria-busy="false"`, and the next swap shows the same element again. The
 * sign-in swap to the interim root, the largest download, is the first case.
 * The interim root also waits for its stylesheet's `load` or `error` before it mounts, as the
 * render-blocking `<link>` of the old `main.tsx` did (design-2).
 */
import type { SurfaceServices, SurfaceTarget } from '@zion/sdk-surfaces/v1';
import type { SurfaceMount } from '@zion/sdk-surfaces/v1';
import {
  SYSTEM_OUTPUT_NAMES,
  type MaintenanceNotice,
  type SystemOutputName,
  type SystemSurfaceInput,
  type SystemSurfaceName,
} from '@zion/shell-system-surfaces/protocol';
import { OVERLAY_ELEMENT_ID, type SurfacesRuntime } from '@zion/surface-host';
import { renderFatalScreen, type FatalReason } from './fatal-screen.js';
import { INTERIM_ROOT_TARGET, SYSTEM_TARGET, type InterimRootAsset, type LoaderHost } from './loader-host.js';
import { systemBrand } from './system-input.js';

/** The longest an outgoing wrapper stays attached: the runtime's one-second unmount deadline, plus margin. */
export const WRAPPER_REMOVAL_MS = 1_500;

/** The class of the boot indicator `index.html` puts in `#root`, and of the one the controller creates when `#root` has none. */
export const BOOT_INDICATOR_CLASS = 'zion-boot';

/** The text of a boot indicator the controller creates; `index.html` carries the same text. */
export const BOOT_INDICATOR_TEXT = 'Loading';

/** What `#root` holds now. */
export type MountedKind = 'booting' | 'root' | 'interim-root' | 'system' | 'fatal';

/** What `#root` holds now, with the facts of each kind. */
export type MountedState =
  | { readonly kind: 'booting' }
  | { readonly kind: 'root'; readonly target: SurfaceTarget }
  | { readonly kind: 'interim-root' }
  | { readonly kind: 'system'; readonly surface: SystemSurfaceName }
  | { readonly kind: 'fatal'; readonly reason: FatalReason };

/** What the controller reads and calls. */
export interface MountControllerDeps {
  readonly rootEl: HTMLElement;
  readonly maintenanceEl: HTMLElement;
  readonly doc: Document;
  readonly runtime: SurfacesRuntime;
  readonly host: LoaderHost;
  readonly interimRoot: InterimRootAsset | null;
  /** Build a system page's input; the controller supplies the page, the brand row and the notice. */
  readonly systemInput: (
    surface: SystemSurfaceName,
    brand: SystemSurfaceInput['brand'],
    maintenance: MaintenanceNotice | null,
  ) => SystemSurfaceInput;
  /** Receives every output the `#root` system mount emits, before any check. */
  readonly onSystemOutput: (output: SystemOutputName, payload: unknown) => void;
}

/**
 * The controller.
 *
 * - `kind` starts `booting`, with the boot indicator in `#root`. Every swap of `#root` shows the
 *   indicator (`div.zion-boot[role=status]`, created when `#root` has none), sets
 *   `aria-busy="true"` and mounts into a hidden wrapper. When the incoming mount's `ready`
 *   settles `mounted`, the controller hides the indicator, sets `aria-busy="false"` and shows the
 *   wrapper; the indicator element stays in `#root` for the next swap (R-62). A mount that fails
 *   goes to its failure page, which is a swap like any other, or to the fatal screen, which
 *   replaces the indicator and removes `aria-busy`.
 * - `showSystem(surface)` updates a live `#root` system mount with the new page, or swaps `#root`
 *   to a system mount of {@link SYSTEM_TARGET} and listens to its three outputs. Once mounted it
 *   subscribes to that mount's `branding` and `polarity`; each change calls `refreshSystem`.
 *   Resolves once the mount settles. A mount that ends `failed` or `crashed`, or crashes later,
 *   shows `fatal: system-bundle`.
 * - `refreshSystem()` sends a fresh input to every live system mount, `#root` and maintenance.
 * - `showRoot(target)` swaps `#root` to the target and resolves once it settles. `failed` with
 *   `unresolved` shows the `not-found` system page; any other failure, a crash at mount, or a later
 *   `surface:crashed` for the live root shows `fatal: root-mount`.
 * - `showInterimRoot()` links the interim stylesheet once (`link[data-zion-interim-css]`, removed by
 *   the next swap of `#root`), waits for the link's `load` or `error` (an `error` mounts the root
 *   unstyled and logs a warning), then mounts {@link INTERIM_ROOT_TARGET} with `showRoot`'s
 *   outcomes. A swap or a fatal screen during the wait removes the link and settles the call with
 *   no mount. Without an interim root it shows `not-found`.
 * - `showFatal(reason)` unmounts both points and renders the fatal screen into `#root`. A second
 *   call keeps the first reason.
 * - `showMaintenance(notice)` mounts the `maintenance` page into `#zion-maintenance`, or updates
 *   the live one; `hideMaintenance()` unmounts it. Neither changes what `#root` holds. The page is
 *   a modal dialog, so while its mount is live the controller sets `inert` on `#root` and on
 *   `#zion-overlay` (when the document has one): keyboard focus and pointer input cannot reach the
 *   page under the backdrop. `hideMaintenance` and `showFatal` remove `inert` from both, so the
 *   fatal screen's Reload button takes focus (review finding design-11).
 *
 * A swap that a later swap overtakes settles without changing anything. Nothing here rejects.
 */
export interface MountController {
  readonly kind: MountedKind;
  /** What `#root` holds, with the page, the target or the fatal reason. */
  readonly shown: MountedState;
  showSystem(surface: SystemSurfaceName): Promise<void>;
  refreshSystem(): void;
  showRoot(target: SurfaceTarget): Promise<void>;
  showInterimRoot(): Promise<void>;
  showFatal(reason: FatalReason): void;
  showMaintenance(notice: MaintenanceNotice): void;
  hideMaintenance(): void;
}

interface Slot {
  readonly mount: SurfaceMount;
  readonly wrapper: HTMLElement;
  readonly cleanups: Array<() => void>;
}

interface SystemSlot extends Slot {
  surface: SystemSurfaceName;
  notice: MaintenanceNotice | null;
}

/** Create the controller over the two mount points. No side effect until the first `show*`. */
export function createMountController(deps: MountControllerDeps): MountController {
  const { rootEl, maintenanceEl, doc, runtime, host } = deps;
  let shown: MountedState = { kind: 'booting' };
  let rootSlot: Slot | null = null;
  let systemSlot: SystemSlot | null = null;
  let maintenanceSlot: SystemSlot | null = null;
  let interimLink: HTMLLinkElement | null = null;
  /** Settles a pending stylesheet wait with "dropped", so a swap during the wait never strands it. */
  let dropInterimWait: (() => void) | null = null;

  const servicesOf = (slot: Slot | null): SurfaceServices | null => (slot === null ? null : host.servicesOf(slot.mount.id));

  const brand = (): SystemSurfaceInput['brand'] => systemBrand(servicesOf(systemSlot) ?? servicesOf(maintenanceSlot));

  const retire = (slot: Slot): void => {
    // Every cleanup is an unsubscribe, idempotent and non-throwing by the runtime's and WS-8's contracts.
    for (const cleanup of slot.cleanups.splice(0)) cleanup();
    slot.mount.unmount();
    const { wrapper } = slot;
    wrapper.hidden = true;
    const remove = (): void => {
      observer.disconnect();
      clearTimeout(timer);
      wrapper.remove();
    };
    const observer = new MutationObserver(() => {
      if (wrapper.childNodes.length === 0) remove();
    });
    observer.observe(wrapper, { childList: true });
    const timer = setTimeout(remove, WRAPPER_REMOVAL_MS);
    if (wrapper.childNodes.length === 0) remove();
  };

  const dropInterimLink = (): void => {
    interimLink?.remove();
    interimLink = null;
    dropInterimWait?.();
    dropInterimWait = null;
  };

  /** The boot indicator among `#root`'s children, created first in `#root` when there is none. */
  const indicator = (): HTMLElement => {
    const found = rootEl.querySelector<HTMLElement>(`:scope > .${BOOT_INDICATOR_CLASS}`);
    if (found !== null) return found;
    const created = doc.createElement('div');
    created.className = BOOT_INDICATOR_CLASS;
    created.setAttribute('role', 'status');
    created.textContent = BOOT_INDICATOR_TEXT;
    rootEl.prepend(created);
    return created;
  };

  /** Start a swap of `#root`: retire the live mount, show the indicator and mark `#root` busy (R-62). */
  const beginSwap = (): void => {
    if (rootSlot !== null) retire(rootSlot);
    rootSlot = null;
    systemSlot = null;
    dropInterimLink();
    indicator().hidden = false;
    rootEl.setAttribute('aria-busy', 'true');
  };

  /**
   * End a swap whose mount settled `mounted`: hide the indicator, set `aria-busy="false"`, show the
   * wrapper. Any other child that is neither a mount wrapper nor the indicator (a fatal box from a
   * boot step before the controller) leaves `#root`. The indicator stays for the next swap (R-62).
   */
  const reveal = (slot: Slot): void => {
    const shownIndicator = indicator();
    for (const child of Array.from(rootEl.childNodes)) {
      if (child === shownIndicator) continue;
      if (!(child instanceof HTMLElement && child.hasAttribute('data-zion-mount'))) child.remove();
    }
    shownIndicator.hidden = true;
    rootEl.setAttribute('aria-busy', 'false');
    slot.wrapper.hidden = false;
  };

  const newWrapper = (parent: HTMLElement): HTMLElement => {
    const wrapper = doc.createElement('div');
    wrapper.setAttribute('data-zion-mount', '');
    // `#root`'s wrapper shows once its mount settles; a maintenance wrapper shows at once.
    if (parent === rootEl) wrapper.hidden = true;
    parent.append(wrapper);
    return wrapper;
  };

  /**
   * Set or remove `inert` on the elements under the maintenance dialog: `#root` and the overlay
   * the embedded surfaces portal into. Only the loader owns those elements (R-9), so no surface can
   * do this (design-11).
   */
  const setUnderDialogInert = (inert: boolean): void => {
    for (const el of [rootEl, doc.getElementById(OVERLAY_ELEMENT_ID)]) {
      if (el === null) continue;
      if (inert) el.setAttribute('inert', '');
      else el.removeAttribute('inert');
    }
  };

  const showFatal = (reason: FatalReason): void => {
    if (shown.kind === 'fatal') return;
    if (rootSlot !== null) retire(rootSlot);
    if (maintenanceSlot !== null) retire(maintenanceSlot);
    setUnderDialogInert(false);
    rootSlot = null;
    systemSlot = null;
    maintenanceSlot = null;
    dropInterimLink();
    shown = { kind: 'fatal', reason };
    renderFatalScreen(rootEl, reason);
  };

  const inputFor = (slot: SystemSlot): SystemSurfaceInput => deps.systemInput(slot.surface, brand(), slot.notice);

  const refreshSystem = (): void => {
    for (const slot of [systemSlot, maintenanceSlot]) {
      if (slot !== null && slot.mount.state === 'mounted') slot.mount.update(inputFor(slot));
    }
  };

  /**
   * Mount a system page into a fresh wrapper in `parent`. A crash shows `fatal: system-bundle`; a
   * retired slot has already dropped this listener, so only a live page's crash reaches it.
   */
  const mountSystem = (parent: HTMLElement, surface: SystemSurfaceName, notice: MaintenanceNotice | null): SystemSlot => {
    const wrapper = newWrapper(parent);
    const input = deps.systemInput(surface, brand(), notice);
    const mount = runtime.mountSurface(wrapper, SYSTEM_TARGET, { input });
    const slot: SystemSlot = { mount, wrapper, cleanups: [], surface, notice };
    slot.cleanups.push(
      mount.onRuntime((event) => {
        if (event.name === 'surface:crashed') showFatal('system-bundle');
      }),
    );
    return slot;
  };

  const watchBrand = (slot: SystemSlot): void => {
    const services = host.servicesOf(slot.mount.id);
    if (services === null) return;
    slot.cleanups.push(services.branding.subscribe(refreshSystem), services.polarity.subscribe(refreshSystem));
    refreshSystem();
  };

  const showSystem = async (surface: SystemSurfaceName): Promise<void> => {
    if (shown.kind === 'fatal') return;
    if (systemSlot !== null) {
      systemSlot.surface = surface;
      shown = { kind: 'system', surface };
      if (systemSlot.mount.state === 'mounted') systemSlot.mount.update(inputFor(systemSlot));
      return;
    }
    beginSwap();
    shown = { kind: 'system', surface };
    const slot = mountSystem(rootEl, surface, null);
    systemSlot = slot;
    rootSlot = slot;
    for (const name of SYSTEM_OUTPUT_NAMES) {
      slot.cleanups.push(slot.mount.on(name, (payload) => deps.onSystemOutput(name, payload)));
    }
    const state = await slot.mount.ready;
    if (slot !== systemSlot) return;
    if (state !== 'mounted') {
      showFatal('system-bundle');
      return;
    }
    reveal(slot);
    watchBrand(slot);
  };

  const mountRoot = async (target: SurfaceTarget, next: 'root' | 'interim-root'): Promise<void> => {
    const wrapper = newWrapper(rootEl);
    const mount = runtime.mountSurface(wrapper, target);
    const slot: Slot = { mount, wrapper, cleanups: [] };
    rootSlot = slot;
    shown = next === 'root' ? { kind: 'root', target } : { kind: 'interim-root' };
    let failure: string | null = null;
    slot.cleanups.push(
      mount.onRuntime((event) => {
        if (event.name === 'surface:load-failed') failure = event.code;
        // A retired slot has already dropped this listener, so only the live root's crash reaches it.
        if (event.name === 'surface:crashed') showFatal('root-mount');
      }),
    );
    const state = await mount.ready;
    if (rootSlot !== slot) return;
    if (state === 'mounted') {
      reveal(slot);
      return;
    }
    // A crash has already shown the fatal screen through the listener above and retired this
    // slot, so a `failed` mount is the one state that reaches here; any other reads as fatal.
    if (failure === 'unresolved' && next === 'root') await showSystem('not-found');
    else showFatal('root-mount');
  };

  /**
   * Resolve `true` once `link` fires `load` or `error`, or `false` when a swap or the fatal screen
   * drops the link first. An `error` logs a warning: the root then mounts unstyled.
   */
  const stylesheetSettled = (link: HTMLLinkElement): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      link.addEventListener('load', () => resolve(true), { once: true });
      link.addEventListener(
        'error',
        () => {
          console.warn(`[shell] the interim root stylesheet failed to load: ${link.href}`);
          resolve(true);
        },
        { once: true },
      );
      dropInterimWait = () => resolve(false);
    });

  return {
    get kind() {
      return shown.kind;
    },
    get shown() {
      return shown;
    },
    showSystem,
    refreshSystem,
    async showRoot(target) {
      if (shown.kind === 'fatal') return;
      beginSwap();
      await mountRoot(target, 'root');
    },
    async showInterimRoot() {
      if (shown.kind === 'fatal') return;
      if (deps.interimRoot === null) {
        await showSystem('not-found');
        return;
      }
      beginSwap();
      shown = { kind: 'interim-root' };
      const link = doc.createElement('link');
      link.rel = 'stylesheet';
      link.href = deps.interimRoot.cssUrl;
      link.setAttribute('data-zion-interim-css', '');
      const settled = stylesheetSettled(link);
      doc.head.append(link);
      interimLink = link;
      if (!(await settled)) return;
      dropInterimWait = null;
      await mountRoot(INTERIM_ROOT_TARGET, 'interim-root');
    },
    showFatal,
    showMaintenance(notice) {
      if (shown.kind === 'fatal') return;
      if (maintenanceSlot !== null) {
        maintenanceSlot.notice = notice;
        if (maintenanceSlot.mount.state === 'mounted') maintenanceSlot.mount.update(inputFor(maintenanceSlot));
        return;
      }
      const slot = mountSystem(maintenanceEl, 'maintenance', notice);
      maintenanceSlot = slot;
      setUnderDialogInert(true);
      void slot.mount.ready.then((state) => {
        if (slot !== maintenanceSlot) return;
        if (state !== 'mounted') {
          showFatal('system-bundle');
          return;
        }
        // A notice that changed while the page mounted reaches it now.
        slot.mount.update(inputFor(slot));
      });
    },
    hideMaintenance() {
      if (maintenanceSlot === null) return;
      retire(maintenanceSlot);
      maintenanceSlot = null;
      setUnderDialogInert(false);
    },
  };
}
